"use client";
import React, { useRef, useEffect } from "react";
import { X } from "lucide-react";
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
   Button
} from "@/app/components/ui";
import { cn } from "@/app/lib/utils";

interface AlertDialogConfirmProps {
   isOpen: boolean;
   setOpen: (show: boolean) => void;
   onConfirm?: (e: React.MouseEvent<HTMLElement>) => void;
   title?: string;
   description?: string | React.ReactNode;
   cancelText?: string;
   confirmText?: string;
   cancelButtonClassName?: string;
   confirmButtonClassName?: string;
   showCloseButton?: boolean;
   closeOnOverlayClick?: boolean;
   className?: string;
   headerClassName?: string;
   titleClassName?: string;
   descriptionClassName?: string;
   footerClassName?: string;
}

export const AlertDialogConfirm: React.FC<AlertDialogConfirmProps> = ({
   isOpen,
   setOpen,
   onConfirm = () => {},
   title = "title",
   description = "description",
   cancelText = "",
   confirmText = "",
   confirmButtonClassName = "",
   cancelButtonClassName = "",
   showCloseButton = true,
   closeOnOverlayClick = false,
   className = "",
   headerClassName = "",
   titleClassName = "",
   descriptionClassName = "",
   footerClassName = ""
}) => {
   const contentRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         if (
            closeOnOverlayClick &&
            contentRef.current &&
            !contentRef.current.contains(event.target as Node)
         ) {
            setOpen(false);
         }
      };

      if (isOpen) {
         document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
         document.removeEventListener("mousedown", handleClickOutside);
      };
   }, [isOpen, setOpen, closeOnOverlayClick]);

   return (
      <AlertDialog open={isOpen} onOpenChange={setOpen}>
         <AlertDialogContent
            ref={contentRef}
            className={cn("max-w-[650px] p-7", className)}
         >
            {showCloseButton && (
               <Button
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                     e.stopPropagation();
                     setOpen(false);
                  }}
                  className="absolute right-4 top-4 rounded-xl opacity-70 hover:opacity-100"
               >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
               </Button>
            )}
            <AlertDialogHeader className={headerClassName}>
               <AlertDialogTitle className={titleClassName}>{title}</AlertDialogTitle>
               <AlertDialogDescription className={descriptionClassName}>
                  {description}
               </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className={footerClassName}>
               {cancelText && (
                  <AlertDialogCancel className={cancelButtonClassName}>
                     {cancelText}
                  </AlertDialogCancel>
               )}
               {confirmText && (
                  <AlertDialogAction
                     onClick={onConfirm}
                     className={confirmButtonClassName}
                  >
                     {confirmText}
                  </AlertDialogAction>
               )}
            </AlertDialogFooter>
         </AlertDialogContent>
      </AlertDialog>
   );
};
